// Email Service - Frontend API Client
// Handles communication with backend email API endpoints

import axios, { AxiosInstance } from "axios";
import {
  EmailConfig,
  EmailMessage,
  EmailSendResponse,
  EmailTestResponse,
  EmailSetupFormData,
  EmailTemplate,
  EmailLog,
} from "../types";

class EmailService {
  private api: AxiosInstance;
  private baseUrl: string;

  constructor() {
    this.baseUrl =
      process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";
    console.log(`[EmailService] Initializing with baseURL: ${this.baseUrl}`);

    this.api = axios.create({
      baseURL: this.baseUrl,
      timeout: parseInt(process.env.REACT_APP_API_TIMEOUT || "10000"),
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    });

    console.log(
      "[EmailService] Axios instance created with timeout:",
      parseInt(process.env.REACT_APP_API_TIMEOUT || "10000"),
      "ms"
    );

    // Add request interceptor to include auth token
    this.api.interceptors.request.use(
      (config) => {
        const token =
          localStorage.getItem("authToken") ||
          sessionStorage.getItem("authToken");
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
          console.log(
            `[EmailService] Request to ${config.url} - Auth token attached`
          );
        } else {
          console.warn(
            `[EmailService] Request to ${config.url} - No auth token found`
          );
        }

        // Add API key if available (matching SampleDataGrid pattern)
        if (process.env.REACT_APP_API_KEY) {
          config.headers["X-API-Key"] = process.env.REACT_APP_API_KEY;
        }

        return config;
      },
      (error) => {
        console.error("[EmailService] Request interceptor error:", error);
        return Promise.reject(error);
      }
    );

    // Add response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => {
        console.log(
          `[EmailService] Response from ${response.config.url} - Status: ${response.status}`
        );
        return response;
      },
      (error) => {
        if (error.response?.status === 401) {
          // Handle unauthorized access
          console.error(
            "[EmailService] Unauthorized access (401) - Token may be invalid or expired"
          );
        } else if (error.response) {
          console.error(
            `[EmailService] API error - Status: ${error.response.status}, URL: ${error.config?.url}`
          );
        } else if (error.request) {
          console.error(
            "[EmailService] No response received from server:",
            error.message
          );
        } else {
          console.error("[EmailService] Request setup error:", error.message);
        }
        return Promise.reject(error);
      }
    );

    console.log("[EmailService] Interceptors configured successfully");
  }

  /**
   * Send an email
   */
  async sendEmail(message: EmailMessage): Promise<EmailSendResponse> {
    console.log("[EmailService] sendEmail - Request:", {
      to: message.to,
      subject: message.subject,
      hasHtml: !!message.html,
      hasText: !!message.text,
      attachments: message.attachments?.length || 0,
    });
    try {
      const response = await this.api.post<EmailSendResponse>(
        "/email/send",
        message
      );
      console.log("[EmailService] sendEmail - Success:", response.data);
      return response.data;
    } catch (error: any) {
      console.error("[EmailService] sendEmail - Error:", {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
      });
      return {
        success: false,
        error:
          error.response?.data?.message ||
          error.message ||
          "Failed to send email",
        timestamp: new Date(),
      };
    }
  }

  /**
   * Test email configuration by sending a test email
   */
  async testEmailConfiguration(
    config: EmailSetupFormData,
    testRecipient: string
  ): Promise<EmailTestResponse> {
    console.log(
      "[EmailService] testEmailConfiguration - Testing with recipient:",
      testRecipient
    );
    try {
      const response = await this.api.post<EmailTestResponse>(
        "/email/setup/test",
        {
          ...config,
          testRecipient,
        }
      );
      console.log(
        "[EmailService] testEmailConfiguration - Test result:",
        response.data
      );
      return response.data;
    } catch (error: any) {
      console.error("[EmailService] testEmailConfiguration - Error:", {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
      });
      return {
        success: false,
        message: "Failed to test email configuration",
        error: error.response?.data?.message || error.message,
      };
    }
  }

  /**
   * Save email configuration
   */
  async saveEmailConfiguration(
    config: EmailSetupFormData
  ): Promise<{ success: boolean; message: string }> {
    console.log(
      "[EmailService] saveEmailConfiguration - Saving config for:",
      config.host,
      ":",
      config.port
    );
    try {
      const response = await this.api.post("/email/setup", config);
      console.log(
        "[EmailService] saveEmailConfiguration - Success:",
        response.data
      );
      return {
        success: true,
        message:
          response.data?.message || "Email configuration saved successfully",
      };
    } catch (error: any) {
      console.error("[EmailService] saveEmailConfiguration - Error:", {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
      });
      return {
        success: false,
        message:
          error.response?.data?.message ||
          error.message ||
          "Failed to save email configuration",
      };
    }
  }

  /**
   * Get current email configuration
   */
  async getEmailConfiguration(): Promise<EmailSetupFormData | null> {
    console.log(
      "[EmailService] getEmailConfiguration - Fetching current config"
    );
    try {
      const response = await this.api.get<EmailSetupFormData>("/email/setup");
      console.log(
        "[EmailService] getEmailConfiguration - Success:",
        response.data
      );
      return response.data;
    } catch (error: any) {
      console.error("[EmailService] getEmailConfiguration - Error:", {
        message: error.message,
        status: error.response?.status,
      });
      return null;
    }
  }

  /**
   * Get email templates
   */
  async getEmailTemplates(): Promise<EmailTemplate[]> {
    console.log("[EmailService] getEmailTemplates - Fetching templates");
    try {
      const response = await this.api.get<EmailTemplate[]>("/email/templates");
      console.log(
        "[EmailService] getEmailTemplates - Found",
        response.data?.length || 0,
        "templates"
      );
      return response.data || [];
    } catch (error: any) {
      console.error("[EmailService] getEmailTemplates - Error:", {
        message: error.message,
        status: error.response?.status,
      });
      return [];
    }
  }

  /**
   * Get email template by ID
   */
  async getEmailTemplate(templateId: string): Promise<EmailTemplate | null> {
    console.log(
      "[EmailService] getEmailTemplate - Fetching template:",
      templateId
    );
    try {
      const response = await this.api.get<EmailTemplate>(
        `/email/templates/${templateId}`
      );
      console.log(
        "[EmailService] getEmailTemplate - Success:",
        response.data?.name
      );
      return response.data;
    } catch (error: any) {
      console.error("[EmailService] getEmailTemplate - Error:", {
        templateId,
        message: error.message,
        status: error.response?.status,
      });
      return null;
    }
  }

  /**
   * Render email template with data
   */
  async renderTemplate(
    templateId: string,
    data: Record<string, any>
  ): Promise<{ html: string; text: string } | null> {
    console.log(
      "[EmailService] renderTemplate - Rendering template:",
      templateId,
      "with data keys:",
      Object.keys(data)
    );
    try {
      const response = await this.api.post(
        `/email/templates/${templateId}/render`,
        data
      );
      console.log(
        "[EmailService] renderTemplate - Success, HTML length:",
        response.data?.html?.length || 0
      );
      return response.data;
    } catch (error: any) {
      console.error("[EmailService] renderTemplate - Error:", {
        templateId,
        message: error.message,
        status: error.response?.status,
      });
      return null;
    }
  }

  /**
   * Get email sending history/logs
   */
  async getEmailLogs(
    page: number = 1,
    pageSize: number = 20
  ): Promise<{ logs: EmailLog[]; total: number }> {
    console.log(
      "[EmailService] getEmailLogs - Fetching page:",
      page,
      "pageSize:",
      pageSize
    );
    try {
      const response = await this.api.get("/email/logs", {
        params: { page, pageSize },
      });
      const logsCount = (response.data?.logs || response.data?.data || [])
        .length;
      const total = response.data?.total || response.data?.totalCount || 0;
      console.log(
        "[EmailService] getEmailLogs - Success:",
        logsCount,
        "logs returned, total:",
        total
      );
      return {
        logs: response.data?.logs || response.data?.data || [],
        total,
      };
    } catch (error: any) {
      console.error("[EmailService] getEmailLogs - Error:", {
        message: error.message,
        status: error.response?.status,
        page,
        pageSize,
      });
      return { logs: [], total: 0 };
    }
  }

  /**
   * Validate email address format
   */
  validateEmailAddress(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isValid = emailRegex.test(email);
    console.log(
      "[EmailService] validateEmailAddress -",
      email,
      "is",
      isValid ? "valid" : "invalid"
    );
    return isValid;
  }

  /**
   * Sanitize email content (PLACEHOLDER - NOT FOR PRODUCTION USE)
   * WARNING: This is a basic example only. In production, you MUST use a
   * proper sanitization library like DOMPurify or let the backend handle sanitization.
   * The backend should be the primary line of defense for content sanitization.
   *
   * @param content - The content to sanitize
   * @returns The input content (backend should handle sanitization)
   */
  sanitizeContent(content: string): string {
    // NOTE: This function is intentionally simplified as sanitization
    // should be handled by the backend API before sending emails.
    // The frontend should NOT be trusted for security-critical sanitization.
    console.warn(
      "Frontend sanitization is not sufficient for production. Ensure backend sanitizes all email content."
    );
    return content;
  }
}

// Export singleton instance
export const emailService = new EmailService();
export default emailService;
